import { Box, Flex, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { BuyerParticipationRequestDetailDto } from "../../../../autogen/bff-api";
import { parseCriterionKey } from "../../../../common/qualification/criterionKey";
import { QualificationReviewCard } from "../../bid-detail/QualificationReviewCard";
import { getRequestQualificationProgress, getRequestQualificationRows } from "./requestQualification";
import { useRequestQualificationReviewEditor } from "./useRequestQualificationReviewEditor";

interface Props {
  eventId: string;
  detail: BuyerParticipationRequestDetailDto;
}

export const RequestQualificationsTab = ({ eventId, detail }: Props) => {
  const { t } = useTranslation();
  const editor = useRequestQualificationReviewEditor(eventId, detail.request.id, detail);
  const { hasRequirements, fulfilled, notFulfilled, pending, total } = getRequestQualificationProgress(
    detail.selectionCriteria ?? [],
    detail.request.qualificationResponses ?? [],
  );
  const rows = getRequestQualificationRows(detail);
  const locked = detail.invitationConfirmed;

  if (!hasRequirements) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        gap="2"
        py="16"
        rounded="xl"
        bg="smBackground"
        border="1px dashed"
        borderColor="smBorder"
        color="smMuted"
      >
        <Text fontWeight="medium">{t("No qualification requirements")}</Text>
      </Flex>
    );
  }

  return (
    <Stack spacing="6">
      <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" px="5" py="4">
        <Text color="smMuted" fontSize="sm">
          {pending === 0
            ? fulfilled === total
              ? t("All {{total}} requirements are fulfilled", { total })
              : t("{{count}} of {{total}} fulfilled", { count: fulfilled, total })
            : t("{{count}} of {{total}} reviewed", { count: fulfilled + notFulfilled, total })}
        </Text>
      </Box>

      {rows.map((row) => {
        const parsed = parseCriterionKey(row.criterionKey);
        return (
          <QualificationReviewCard
            key={row.criterionKey}
            type={parsed.type}
            name={parsed.name}
            documentationRequirement={row.criterion.description ?? null}
            supplierDescription={row.description ?? null}
            supplierEvidence={row.evidenceDocuments ?? []}
            verdict={editor.state.verdicts[row.criterionKey] ?? null}
            comment={editor.state.comments[row.criterionKey] ?? ""}
            onVerdictChange={(v) => editor.setVerdict(row.criterionKey, v)}
            onCommentChange={(v) => editor.setComment(row.criterionKey, v)}
            isReadOnly={locked}
          />
        );
      })}
    </Stack>
  );
};
